import { useMemo, useState } from "react";
// MUI
import { Box, Fade } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridActionsCellItem,
	GridColDef,
	GridEventListener,
	GridRenderCellParams,
	GridRenderEditCellParams,
	GridRowId,
	GridRowModes,
	GridRowModesModel,
	GridValidRowModel,
} from "@mui/x-data-grid-pro";

// Context
import { useReconciliationSection } from "@/DataPages/context/inventory/ReconciliationSectionContext";
import { TExtraTableProps } from "@/DataPages/types/inventory/commons";

// Third Party
import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import EditCellSelector from "./components/EditCellSelector";
import EditCellDialog from "./components/EditCellDialog";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";

// Helpers
import RowFeedback from "@/Common/helpers/RowFeedback";

// API
import { updateReconciliation } from "@/DataPages/api/inventory/reconciliation/mutations";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Types
import { ClassNameHints } from "@/Common/types/tables";
import {
	IReconciliation,
	IReconciliationNoteHistory,
	IReconciliationReason,
	IReconciliationResolution,
	IVehicleInfo,
	VEHICLE_INFO_PROPS,
	SPECIAL_VEHICLE_TYPES,
} from "@/DataPages/types/inventory/models/reconciliation";
import { IUpdateReconciliationInput } from "../../types/reconciliation";

// Constants
import { DEFAULT_REASON, DEFAULT_RESOLUTION } from "../../constants/reconciliation";

// Icons
import { ReactComponent as CompletedIcon } from "@/assets/icons/dataPages/inventory/completed.svg";
import { ReactComponent as WarningIcon } from "@/assets/icons/dataPages/inventory/incompleted.svg";
import { ReactComponent as ErrorIcon } from "@/assets/icons/dataPages/inventory/unstarted.svg";
import { ReactComponent as SaveIcon } from "@/Motivators/components/Spiffs/Tables/assets/icons/SaveIcon.svg";
import CancelIcon from "@mui/icons-material/Close";
import { validateNotesField, validateReasonField, validateResolutionField } from "../../helpers/reconciliation";
import { SelectionAndFilterCheckbox } from "../../SelectionAndFilterCheckbox";
import { InventoryTab } from "../../constants";
import { SelectionAndFilterCheckboxAll } from "../../SelectionAndFilterAllCheckbox";

const SELECTABLE_COLUMNS = ["reason", "resolution"];

const statusOptions = [
	{
		predicate: (row: IReconciliation) => !row?.reconciled,
		Icon: ErrorIcon,
		iconClass: "!fill-error",
	},
	{
		predicate: (row: IReconciliation) => row.reconciled && row.keyStates?.filter((ks) => !ks.isConsistent)?.length,
		Icon: WarningIcon,
		iconClass: "!fill-warning",
	},
	{
		predicate: (row: IReconciliation) => row.reconciled && !row.keyStates?.filter((ks) => !ks.isConsistent)?.length,
		Icon: CompletedIcon,
		iconClass: "!fill-success",
	},
];

const createCheckboxColumn = (table: string, rows: IReconciliation[]): GridColDef => ({
	field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
	width: 44,
	filterable: false,
	sortable: false,
	resizable: false,
	renderHeader: () => (
		<SelectionAndFilterCheckboxAll
			className="!ml-px"
			table={table}
			data={rows}
			origin={InventoryTab.RECONCILIATION}
			pathToVinNumber="vehicle.vinNumber"
		/>
	),
	renderCell: (params) => (
		<SelectionAndFilterCheckbox
			_id={params.row._id as string}
			origin={InventoryTab.RECONCILIATION}
			table={table}
			row={params.row as IReconciliation}
			pathToVinNumber="vehicle.vinNumber"
			className=""
		/>
	),
});

type TReconciliationUnitsTableProps = Readonly<{
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	columns: (GridColDef & { hints?: Partial<ClassNameHints> })[];
	extraTableProps?: TExtraTableProps;
	hasNextPage: boolean;
	table: string;
}>;

type TExtraProps = {
	reasons: { isLoading: boolean; data: IReconciliationReason[] };
	resolutions: { isLoading: boolean; data: IReconciliationResolution[] };
};

const ReconciliationUnitsEditableTable = (props: TReconciliationUnitsTableProps) => {
	const { columns, extraTableProps, locationId, commissionCycleId, payPeriodId, hasNextPage, table } = props;
	const {
		reasons: { isLoading: isReasonsLoading, data: reasonOptions },
		resolutions: { isLoading: isResolutionsLoading, data: resolutionOptions },
	} = extraTableProps as TExtraProps;

	const [rowId, setRowId] = useState<GridRowId | null>(null);
	const [selectorOpen, setSelectorOpen] = useState(false);
	const [isNotesPopupOpen, setIsNotesPopupOpen] = useState(false);
	const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
	const { filteredData } = useReconciliationSection();
	const apiRef = useGridApiRef();

	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	/********************************************* MUTATIONS ************************************************/
	const { mutate: mutateUpdateReconciliation } = useMutation({
		mutationKey: ["mutateUpdateReconciliation", locationId, commissionCycleId, payPeriodId],
		mutationFn: (input: IUpdateReconciliationInput) => {
			return updateReconciliation(locationId, commissionCycleId, payPeriodId, input);
		},
		onSettled: () => {
			RowFeedback.skeleton(rowId, apiRef)?.success();
			setRowId(null);
		},
	});

	/********************************************* HANDLERS *************************************************/
	const handleRowEditStart: GridEventListener<"rowEditStart"> | undefined = (_params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	const handleRowEditStop: GridEventListener<"rowEditStop"> | undefined = (_params, event) => {
		event.defaultMuiPrevented = true;
	};

	const handleOnCancelChanges = (id: GridRowId) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});
	};

	const handleOnSaveChanges = (id: GridRowId) => {
		const editingRow = apiRef.current.state.editRows;
		if (!editingRow) {
			return;
		}

		const currentEditingRow = editingRow[id];

		const isReasonValid = !currentEditingRow.reason.error;
		const isResolutionValid = !currentEditingRow.resolution.error;
		const isNotesValid = !currentEditingRow.notes.error;

		if (!isReasonValid && !isResolutionValid && !isNotesValid) {
			return;
		}

		setRowId(id);
		setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: false } });
	};

	const handleProcessRowUpdate = async (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
		const newNotes =
			newRow.notes.notes
				?.filter((note: IReconciliationNoteHistory) => !note._id)
				.map((note: IReconciliationNoteHistory) => note.note) ?? [];

		const { notes, ...oldRowWithoutNotes } = oldRow as IReconciliation;

		const updatedReconciliation: Omit<IReconciliation, "notes"> = {
			...(oldRowWithoutNotes as IReconciliation),
			hasNotes: newRow.notes.notes?.some((note: IReconciliationNoteHistory) => !!note.note),
			reason: newRow.reason,
			resolution: newRow.resolution,
			reconciled: or(oldRow.reconciled, newRow.manuallyReconciled),
			manuallyReconciled: newRow.manuallyReconciled ?? false,
		};

		const mutateUpdateReconciliationInput: IUpdateReconciliationInput = {
			reconciliation: updatedReconciliation,
			notes: newNotes,
		};

		RowFeedback.skeleton(newRow._id, apiRef)?.display();
		mutateUpdateReconciliation(mutateUpdateReconciliationInput);
		return { ...updatedReconciliation, notes: newRow.notes };
	};

	const handleProcessRowUpdateError = () => {
		RowFeedback.skeleton(rowId, apiRef)?.success();
		setRowId(null);
	};

	const renderStatusCell = (params: GridRenderCellParams) => {
		const { row } = params;
		const match = statusOptions.find((option) => option.predicate(row));
		if (!match) {
			return null;
		}
		const { Icon, iconClass } = match;
		return (
			<Box className="!h-full !flex !items-center !justify-center">
				<Icon className={iconClass} />
			</Box>
		);
	};

	const renderSelectorCell = (
		params: GridRenderCellParams,
		column: GridColDef & {
			hints?: Partial<ClassNameHints>;
		}
	) => {
		const defaultTextToShow = column.field === "reason" ? DEFAULT_REASON : DEFAULT_RESOLUTION;
		const valueToShow = params.value || defaultTextToShow;
		return (
			<TypographyCell fontLineHeight="24px" hints={column.hints} render="absolute" hasTooltip={false}>
				{valueToShow}
			</TypographyCell>
		);
	};

	const renderDefaultCell = (
		params: GridRenderCellParams,
		column: GridColDef & {
			hints?: Partial<ClassNameHints>;
		}
	) => {
		return (
			<TypographyCell
				params={params}
				fontLineHeight="24px"
				hints={column.hints}
				render={column.hints?.isMoney ? "absolute" : "default"}
			/>
		);
	};

	const valueGetter = (
		value: never,
		row: IReconciliation,
		column: GridColDef & {
			hints?: Partial<ClassNameHints>;
		}
	) => {
		if (column.field === "type") {
			const gpsVehicleType = row.gps?.gpsVehicleType ?? "";
			if (SPECIAL_VEHICLE_TYPES.includes(gpsVehicleType.toUpperCase() ?? "")) {
				return gpsVehicleType;
			}
			return row.vehicle?.type;
		}
		if (column.field === "name") {
			const gpsAddress = row.gps?.address ?? "";
			return row.lot ? row.lot.name : gpsAddress;
		}
		if (VEHICLE_INFO_PROPS.includes(column.field)) {
			return row.vehicle ? row.vehicle[column.field as keyof IVehicleInfo] : "";
		}
		return value;
	};

	const renderCell = (
		column: GridColDef & {
			hints?: Partial<ClassNameHints>;
		},
		params: GridRenderCellParams
	) => {
		if (column.field === "status") {
			return renderStatusCell(params);
		}
		if (column.field === "reason" || column.field === "resolution") {
			return renderSelectorCell(params, column);
		}
		if (column.field === "notes") {
			return <EditCellDialog apiRef={apiRef} params={params} editable={false} onChangePopupVisibility={setIsNotesPopupOpen} />;
		}
		return renderDefaultCell(params, column);
	};

	const renderEditCell = (params: GridRenderEditCellParams) => {
		const { field } = params;
		if (!SELECTABLE_COLUMNS.includes(field)) {
			return <EditCellDialog apiRef={apiRef} params={params} editable={true} onChangePopupVisibility={setIsNotesPopupOpen} />;
		}

		const isReasonsLoadingOptions = and(field === "reason", isReasonsLoading);
		const isResolutionsLoadingOptions = and(field === "resolution", isResolutionsLoading);

		return (
			<EditCellSelector
				params={params}
				apiRef={apiRef}
				reasonsOptions={reasonOptions}
				resolutionsOptions={resolutionOptions}
				isLoading={or(isReasonsLoadingOptions, isResolutionsLoadingOptions)}
				popperProps={{
					sx: { minHeight: "240px", minWidth: "116px" },
				}}
				onOpenAction={() => setSelectorOpen(true)}
				onCloseAction={() => setSelectorOpen(false)}
			/>
		);
	};

	const actionsColumn: GridColDef = {
		field: "actions",
		type: "actions",
		align: "right",
		width: 70,
		cellClassName: "!px-0",
		getActions: (params) => {
			const { id } = params;
			const isInEditMode = rowModesModel?.[id]?.mode === GridRowModes.Edit;
			if (!isInEditMode) {
				return [];
			}

			return [
				<GridActionsCellItem
					key="save"
					icon={
						<SaveIcon
							className={clsx("!h-4 !w-4", {
								"!fill-default": !selectorOpen,
								"!fill-disabled": selectorOpen,
							})}
						/>
					}
					label="Save"
					onClick={() => {
						handleOnSaveChanges(id);
					}}
					color="inherit"
					disabled={selectorOpen}
				/>,
				<GridActionsCellItem
					key="cancel"
					icon={
						<CancelIcon
							className={clsx("!h-5 !w-5", {
								"!fill-default": !selectorOpen,
								"!fill-disabled": selectorOpen,
							})}
						/>
					}
					label="Cancel"
					onClick={() => handleOnCancelChanges(id)}
					color="inherit"
					disabled={selectorOpen}
				/>,
			];
		},
	};

	const transformedColumns: (GridColDef & { hints?: Partial<ClassNameHints> })[] = columns.map((column) => {
		return {
			field: column.field,
			headerName: column.headerName,
			type: column.type,
			width: column.width,
			align: column.align,
			editable: column.editable,
			headerAlign: column.headerAlign,
			cellClassName: column.cellClassName,
			renderHeader: (params) => <TypographyHeader params={params} />,
			valueGetter: (value, row) => valueGetter(value, row, column),
			renderCell: (params) => renderCell(column, params),
			renderEditCell: (params) => renderEditCell(params),
			preProcessEditCellProps: (params) => {
				if (column.field === "reason") {
					return validateReasonField(params, resolutionOptions ?? []);
				}
				if (column.field === "resolution") {
					return validateResolutionField(params, reasonOptions ?? []);
				}
				if (column.field === "notes") {
					return validateNotesField(params, reasonOptions ?? [], resolutionOptions ?? []);
				}
				return { ...params.props, error: false };
			},
		};
	});

	const checkboxColumn = useMemo(() => createCheckboxColumn(table, filteredData), [table, filteredData]);

	const completeColumns = [checkboxColumn, ...transformedColumns, actionsColumn];
	const toolTipTitle = "Double-click on any row to edit the information for that entry.";
	return (
		<DataSmartTable
			columns={completeColumns}
			rows={filteredData as GridValidRowModel[]}
			apiRef={apiRef}
			headerHeight={40}
			rowHeight={28}
			maxRows={10}
			editMode="row"
			filterStoreConfig={{
				key: `reconciliation_${table}`,
			}}
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			onRowEditStart={handleRowEditStart}
			onRowEditStop={handleRowEditStop}
			rowModesModel={rowModesModel}
			checkboxSelection
			processRowUpdate={handleProcessRowUpdate}
			onProcessRowUpdateError={handleProcessRowUpdateError}
			onRowModesModelChange={(newModel) => {
				if (isNotesPopupOpen) {
					return;
				}
				setRowModesModel(newModel);
			}}
			columnVisibilityModel={{
				_id: false,
				manuallyReconciled: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					customComponent: (
						<>
							<HelpTooltip title={toolTipTitle} />
							{hasNextPage && (
								<Fade in={hasNextPage} timeout={400}>
									<Box className="my-[16px]">
										<LoadingSpinner label="Reconciliation data is still loading..." className="items-center" />
									</Box>
								</Fade>
							)}
						</>
					),
					filter: {
						hide: false,
						disabled: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No Units Data",
					className: "!text-montserrat",
				},
				noResultsOverlay: {
					title: "No Units Data",
					className: "!text-montserrat",
				},
			}}
			customProps={{
				tableContainerStyle: { height: "fit-content", width: "100%", minHeight: "initial" },
				tableClassName: {
					"functional-table": true,
					"display-select-all-checkbox": true,
				},
				expandableRows: false,
				hide: false,
			}}
		/>
	);
};

export default ReconciliationUnitsEditableTable;
